import { collection, addDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Test function to verify email pipeline is working
 * Call this from browser console: window.testEmail()
 */
export async function testEmailQueue() {
  console.log('🧪 Starting email queue test...');
  
  try {
    // Step 1: Check if email routing is configured for 'complications'
    console.log('1️⃣ Checking email routing configuration...');
    const routingRef = doc(db, 'emailRouting', 'complications');
    const routingSnap = await getDoc(routingRef);
    
    if (!routingSnap.exists()) {
      console.error('❌ Email routing not configured for "complications"');
      console.log('💡 Go to Settings → System Settings → Email Routing Configuration');
      console.log('💡 Add an email address for "complications" category');
      return { success: false, error: 'Email routing not configured' };
    }
    
    const routing = routingSnap.data();
    console.log('✅ Email routing found:', routing);
    
    // Step 2: Create a test mailQueue document
    console.log('2️⃣ Creating test mailQueue document...');
    const testPayload = {
      subject: '🧪 Test Email - Email Queue Function Test',
      text: 'This is a test email to verify the email queue system is working correctly.',
      html: `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2>🧪 Test Email</h2>
          <p>This is a test email to verify the email queue system is working correctly.</p>
          <p><strong>Category:</strong> complications</p>
          <p><strong>Test Time:</strong> ${new Date().toISOString()}</p>
        </div>
      `,
      formData: { test: true, timestamp: new Date().toISOString() }
    };
    
    const queueRef = await addDoc(collection(db, 'mailQueue'), {
      category: 'complications',
      payload: testPayload,
      createdAt: serverTimestamp(),
      status: 'pending',
      test: true // Mark as test
    });
    
    console.log('✅ mailQueue document created:', queueRef.id);
    
    // Step 3: Wait a moment, then check if mail document was created
    console.log('3️⃣ Waiting for Cloud Function to process (5 seconds)...');
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    // Check mailQueue document status
    const queueDoc = await getDoc(queueRef);
    if (queueDoc.exists()) {
      console.log('📋 mailQueue document:', queueDoc.data());
    }
    
    // Step 4: Check if mail document was created
    console.log('4️⃣ Checking if mail document was created...');
    
    // Note: We can't easily query the mail collection without knowing the doc ID
    // But we can check Cloud Function logs
    console.log('💡 Check Firebase Console → Functions → Logs for processMailQueue function');
    console.log('💡 Check Firestore → mail collection for new documents');
    
    return {
      success: true,
      queueId: queueRef.id,
      routing: routing,
      message: 'Test email queued. Check Firebase Console logs and mail collection.'
    };
    
  } catch (error) {
    console.error('❌ Test failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Direct test: Create a mail document directly (bypassing queue)
 * This tests if the Firebase Extension is working
 */
export async function testDirectMail() {
  console.log('🧪 Starting direct mail test (bypassing queue)...');
  
  try {
    // Step 1: Check if email routing is configured
    const routingRef = doc(db, 'emailRouting', 'complications');
    const routingSnap = await getDoc(routingRef);
    
    if (!routingSnap.exists()) {
      console.error('❌ Email routing not configured for "complications"');
      return { success: false, error: 'Email routing not configured' };
    }
    
    const routing = routingSnap.data();
    console.log('✅ Email routing found:', routing);
    
    // Step 2: Create mail document directly (as Cloud Function would)
    console.log('2️⃣ Creating mail document directly...');
    
    const mailData = {
      to: routing.to || ['test@example.com'],
      message: {
        subject: '🧪 Direct Test Email - Extension Test',
        text: 'This is a direct test email to verify the Firebase Extension is working.',
        html: `
          <div style="font-family: Arial, sans-serif; padding: 20px;">
            <h2>🧪 Direct Test Email</h2>
            <p>This is a direct test email to verify the Firebase Extension is working.</p>
            <p><strong>Category:</strong> complications</p>
            <p><strong>Test Time:</strong> ${new Date().toISOString()}</p>
          </div>
        `
      },
      createdAt: serverTimestamp(),
      test: true
    };
    
    const mailRef = await addDoc(collection(db, 'mail'), mailData);
    console.log('✅ Mail document created:', mailRef.id);
    console.log('💡 The Firebase Extension should process this and send an email');
    
    return {
      success: true,
      mailId: mailRef.id,
      message: 'Direct mail document created. Extension should process it.'
    };
    
  } catch (error) {
    console.error('❌ Direct test failed:', error);
    return { success: false, error: error.message };
  }
}

// Make functions available globally for console testing
if (typeof window !== 'undefined') {
  window.testEmail = testEmailQueue;
  window.testEmailDirect = testDirectMail;
}

